import { useState } from 'react';

import {
  type EscolhaClassificacao,
  escolhaDoValor,
  opcoesDeClassificacao,
  useClassificacoesPersonalizadas,
} from '@/lib/classificacao-personalizada';
import { mensagemDeErro } from '@/lib/erros';
import { Dinheiro } from '@/ui/components/dinheiro';
import { SelectPesquisavel } from '@/ui/components/select-pesquisavel';
import { toast } from '@/ui/primitives/aviso';
import { Badge } from '@/ui/primitives/badge';
import {
  type CentroCustoResumo,
  type ClassificacaoPersonalizada,
  type ClassificarDocumentoRequest,
  type DefinirCentroCustoRequest,
  type DocumentoDetalhe,
  type TributoDocumento,
} from '@fiscoai/contracts';

import { useClassificar } from './api';
import { useDefinirCentroCusto } from './api-centro-custo';
import { FaixaFiscal } from './faixa-fiscal';
import { STATUS_CLASSIFICACAO } from './rotulos';
import { SeletorCentroCusto } from './seletor-centro-custo';

const ROTULO_TRIBUTO: Partial<Record<TributoDocumento['tributo'], string>> = {
  ICMS_ST: 'ICMS-ST',
  ICMS_ST_RET: 'ST retido',
  FCP_ST: 'FCP-ST',
  IBS_UF: 'IBS UF',
  IBS_MUN: 'IBS mun.',
};

/** Tributo sem destaque (valor nulo) mostra o CST e "sem destaque" — nunca um R$ 0,00 enganoso. */
function Tributo({ t }: { t: TributoDocumento }) {
  return (
    <Badge tom={t.tributo.startsWith('IBS') || t.tributo === 'CBS' ? 'info' : 'neutro'}>
      {ROTULO_TRIBUTO[t.tributo] ?? t.tributo}
      {t.cst && <span className="font-mono text-texto-fraco">CST {t.cst}</span>}
      {t.valor === null ? (
        <span className="text-texto-fraco">sem destaque</span>
      ) : (
        <Dinheiro valor={t.valor} />
      )}
    </Badge>
  );
}

/** Classifica / define o centro de custo e destaca, uma vez, os itens que acabaram de mudar. */
function useClassificacao(doc: DocumentoDetalhe) {
  const classificar = useClassificar(doc.id);
  const centroCusto = useDefinirCentroCusto(doc.id);
  const [destacados, setDestacados] = useState<ReadonlySet<number>>(new Set());
  const todosOsItens = doc.itens.map((i) => i.nItem);
  const aoTerminar = (itens: number[]) => ({
    onSuccess: () => {
      setDestacados(new Set(itens));
    },
    onError: (erro: unknown) => toast.error(mensagemDeErro(erro)),
  });
  const enviar = (corpo: ClassificarDocumentoRequest, itens: number[]) => {
    classificar.mutate(corpo, aoTerminar(itens));
  };
  const enviarCc = (corpo: DefinirCentroCustoRequest, itens: number[]) => {
    centroCusto.mutate(corpo, aoTerminar(itens));
  };
  return {
    destacados,
    todos: ({ classificacao, classificacaoPersonalizadaId: id }: EscolhaClassificacao) => {
      enviar({ todos: classificacao, ...(id ? { todosPersonalizadaId: id } : {}) }, todosOsItens);
    },
    item: (nItem: number, escolha: EscolhaClassificacao) => {
      enviar({ itens: [{ nItem, ...escolha }] }, [nItem]);
    },
    ccTodos: (id: string | null) => {
      enviarCc({ todos: id }, todosOsItens);
    },
    ccItem: (nItem: number, id: string | null) => {
      enviarCc({ itens: [{ nItem, centroCustoId: id }] }, [nItem]);
    },
  };
}

const SEM_PERSONALIZADAS: readonly ClassificacaoPersonalizada[] = [];

/** O centro de custo comum a todos os itens (ou nulo se diferem ou faltam). */
function centroCustoDaNota(doc: DocumentoDetalhe): CentroCustoResumo | null {
  const [primeiro, ...resto] = doc.classificacaoItens;
  const cc = primeiro?.centroCusto ?? null;
  return cc && resto.every((i) => i.centroCusto?.id === cc.id) ? cc : null;
}

/** Barra da nota: andamento da classificação e "classificar a nota inteira" num clique. */
function BarraClassificacao({
  doc,
  podeEditar,
  personalizadas,
  aoClassificarTodos,
  aoDefinirCentroCusto,
}: {
  doc: DocumentoDetalhe;
  podeEditar: boolean;
  personalizadas: readonly ClassificacaoPersonalizada[];
  aoClassificarTodos: (escolha: EscolhaClassificacao) => void;
  aoDefinirCentroCusto: (id: string | null) => void;
}) {
  const status = STATUS_CLASSIFICACAO[doc.statusClassificacao];
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Badge tom={status.tom}>{status.rotulo}</Badge>
      {podeEditar && doc.statusClassificacao !== 'NAO_SE_APLICA' && (
        <div className="flex flex-wrap gap-2">
          <SelectPesquisavel
            aria-label="Classificar nota inteira"
            rotuloBusca="Buscar classificação"
            className="w-56"
            tamanho="sm"
            placeholder="Classificar nota inteira"
            opcoes={opcoesDeClassificacao(personalizadas)}
            valor={null}
            aoMudar={(v) => {
              if (v) aoClassificarTodos(escolhaDoValor(v));
            }}
          />
          <SeletorCentroCusto
            aria-label="Centro de custo da nota inteira"
            placeholder="Centro de custo da nota"
            className="w-56"
            tamanho="sm"
            empresaId={doc.empresaId}
            valor={centroCustoDaNota(doc)}
            aoMudar={aoDefinirCentroCusto}
          />
        </div>
      )}
    </div>
  );
}

/** Um cartão por item: descrição, NCM/CFOP, quantidade × valor, tributos e a faixa fiscal. */
export function AbaItens({
  doc,
  podeClassificar,
}: {
  doc: DocumentoDetalhe;
  podeClassificar: boolean;
}) {
  const classificacao = useClassificacao(doc);
  const editavel = podeClassificar && doc.statusClassificacao !== 'NAO_SE_APLICA';
  const personalizadas =
    useClassificacoesPersonalizadas(doc.empresaId, editavel).data?.items ?? SEM_PERSONALIZADAS;
  const porItem = new Map(doc.classificacaoItens.map((c) => [c.nItem, c]));
  return (
    <div className="grid gap-3">
      <BarraClassificacao
        doc={doc}
        podeEditar={podeClassificar}
        personalizadas={personalizadas}
        aoClassificarTodos={classificacao.todos}
        aoDefinirCentroCusto={classificacao.ccTodos}
      />
      <ol className="grid gap-3">
        {doc.itens.map((item) => (
          <li key={item.nItem} className="grid gap-2 rounded-cartao border border-borda p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="grid min-w-0 gap-0.5">
                <span className="font-medium">
                  <span className="mr-2 tabular text-texto-fraco">{item.nItem}</span>
                  {item.descricao}
                </span>
                <span className="flex flex-wrap gap-x-3 text-xs text-texto-suave">
                  <span>Código {item.codigoProduto}</span>
                  {item.ncm && (
                    <span>
                      NCM <span className="font-mono">{item.ncm}</span>
                    </span>
                  )}
                  <span>
                    CFOP <span className="font-mono">{item.cfop}</span>
                  </span>
                </span>
              </div>
              <div className="grid shrink-0 justify-items-end gap-0.5 text-right">
                <Dinheiro valor={item.valorTotal} className="font-semibold" />
                <span className="text-xs text-texto-fraco tabular">
                  {Number(item.quantidade).toLocaleString('pt-BR')} {item.unidade} ×{' '}
                  <Dinheiro valor={item.valorUnitario} />
                </span>
              </div>
            </div>
            {item.tributos.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {item.tributos.map((t, i) => (
                  <Tributo key={`${t.tributo}-${String(i)}`} t={t} />
                ))}
              </div>
            )}
            <ItemFiscal
              cfop={item.cfop}
              classificacao={porItem.get(item.nItem)}
              podeEditar={editavel}
              destacar={classificacao.destacados.has(item.nItem)}
              empresaId={doc.empresaId}
              personalizadas={personalizadas}
              aoClassificar={(c) => {
                classificacao.item(item.nItem, c);
              }}
              aoDefinirCentroCusto={(id) => {
                classificacao.ccItem(item.nItem, id);
              }}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

function ItemFiscal({
  cfop,
  classificacao,
  ...resto
}: {
  cfop: string;
  classificacao: DocumentoDetalhe['classificacaoItens'][number] | undefined;
  podeEditar: boolean;
  destacar: boolean;
  empresaId: string;
  personalizadas: readonly ClassificacaoPersonalizada[];
  aoClassificar: (escolha: EscolhaClassificacao) => void;
  aoDefinirCentroCusto: (id: string | null) => void;
}) {
  return classificacao ? (
    <FaixaFiscal cfopFornecedor={cfop} item={classificacao} {...resto} />
  ) : null;
}
